import { graphXAxisLabelFontSize, renderTitle } from './getSalesAttributionColumns';
import { colorConstants } from 'config/colorConstants';
import { NumericBooleanMap } from 'components/molecules/PricingSimulator/Analytics/TierAnalyticsContainer';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import { formatNumWithKMBAlt } from './helperMethods';

export interface KPI {
    pre: string;
    val: number;
    post: string;
}

interface KPIs {
    current: KPI;
    simulation: KPI;
    change_amount: KPI;
    change_perc: KPI;
    constant: KPI;
    constant_change_amount: KPI;
    constant_change_perc: KPI;
}

export interface Metric {
    metric_name: string;
    kpis: KPIs;
    show_in_chart: boolean;
    unit_type: string;
    associated_type: string;
    showTopBorder?: boolean;
    has_breakeven_metrics?: boolean;
}

export interface RawDataItem {
    name: string;
    data: Metric[];
}

export interface DataItem extends KPIs {
    [key: string]: string | boolean | number | KPI | undefined;
    label: string;
    isExpanded?: boolean;
    parentIndex?: number;
    itemIndex?: number;
    bgColor: string;
    textColor: string;
    showTopBorder?: boolean;
    showBreakevenMetrics?: boolean;
}

export const getAnalyticsTableColumns = (
    rawData: RawDataItem[],
    expanded: NumericBooleanMap,
    setExpanded: React.Dispatch<React.SetStateAction<NumericBooleanMap>>,
    decimalForNonPercentage: number,
    decimalForPercentage: number,
    columnMapping: { [key: string]: string },
    showNegativesInParenthesis: boolean,
) => {
    const sampleKPIs = rawData && Array.isArray(rawData) && rawData.length > 0 ? rawData[0].data[0].kpis : [];
    const kpiKeys = Object.keys(sampleKPIs);

    const columns = ['Scenario Summary(M)', ...kpiKeys, 'dropdown'];
    const tableColumns: any = columns.map((columnKey: string, colIndex) => {
        let columnTitle = '';
        let dataIndex = '';
        if (colIndex === 0) {
            columnTitle = columnKey;
            dataIndex = 'label';
        } else if (colIndex !== columns.length - 1) {
            columnTitle = columnMapping[columnKey];
            dataIndex = columnKey;
        }
        return {
            title: renderTitle(columnTitle, null, undefined, 'text-purpleFont !font-bold', graphXAxisLabelFontSize),
            dataIndex,
            fixed: colIndex === 0 ? 'left' : false,
            width: colIndex === 0 ? 200 : colIndex === columns.length - 1 ? 20 : 160,
            render: (colData: KPI | string, rowData: DataItem, rowIndex: number) => {
                const { parentIndex } = rowData;
                const isParentExpanded = expanded[parentIndex as number];
                const kpiData = colData as KPI;
                const hideRows = rowData.parentIndex !== undefined && !isParentExpanded;
                const isParentRow = parentIndex === undefined;
                if (isParentRow && colIndex === columns.length - 1) {
                    return (
                        <span
                            onClick={() => {
                                if (expanded[rowIndex]) setExpanded({ ...expanded, [rowIndex]: false });
                                else {
                                    setExpanded({ ...expanded, [rowIndex]: true });
                                }
                            }}
                            className="cursor-pointer flex justify-center items-center text-greyFont"
                        >
                            {expanded[rowIndex] ? (
                                <UpOutlined style={{ fontSize: '12px' }} />
                            ) : (
                                <DownOutlined style={{ fontSize: '12px' }} />
                            )}
                        </span>
                    );
                }
                if (!hideRows) {
                    let cellData = '';
                    if (colIndex === 0) cellData = colData as string;
                    else if (
                        showNegativesInParenthesis &&
                        ['change_amount', 'constant_change_amount'].includes(columnKey)
                    ) {
                        const kpiVal =
                            kpiData.post === '%'
                                ? formatNumWithKMBAlt(kpiData.val, decimalForPercentage)
                                : formatNumWithKMBAlt(kpiData.val, decimalForNonPercentage, decimalForNonPercentage);
                        // show negatives in without - sign and inside ()
                        cellData = `${kpiData.pre ?? ''}${kpiVal?.[0] === '-' ? `(${kpiVal.substring(1)})` : kpiVal}${
                            kpiData.post ?? ''
                        }`;
                    } else if (colIndex !== columns.length - 1) {
                        const kpiVal =
                            kpiData.post === '%'
                                ? formatNumWithKMBAlt(kpiData.val, decimalForPercentage)
                                : formatNumWithKMBAlt(kpiData.val, decimalForNonPercentage, decimalForNonPercentage);
                        cellData = `${kpiData.pre ?? ''}${kpiVal}${kpiData.post ?? ''}`;
                    }
                    const renderEmptyCell = ['breakeven_volume', 'breakeven_volume_variance', 'breakeven_percentage'];

                    if (renderEmptyCell.includes(columnKey) && !rowData.showBreakevenMetrics) {
                        return null;
                    }
                    return (
                        <div
                            className={`font-xs ${isParentRow ? 'cursor-pointer' : ''}`}
                            onClick={() => {
                                if (!isParentRow) return;
                                setExpanded((prev) => ({
                                    ...prev,
                                    [rowIndex]: !prev[rowIndex], // Toggles the current state
                                }));
                            }}
                        >
                            {cellData}
                        </div>
                    );
                }
                return null;
            },
            onCell: (rowData: DataItem, rowIndex: number) => {
                const isParentExpanded = expanded[rowData.parentIndex as number];
                const hideRows = rowData.parentIndex !== undefined && !isParentExpanded;

                return {
                    style: {
                        textAlign: 'auto',
                        borderRight: rowIndex === columns.length - 1 ? 'none' : 'auto',
                        backgroundColor: rowData.bgColor,
                        color: rowData.textColor,
                        borderTop: rowData.showTopBorder ? `2px solid ${colorConstants.borderGrey}` : 'auto',
                    },
                    colSpan: hideRows ? 0 : 1,
                };
            },
        };
    });
    return tableColumns;
};

export const transformData = (
    rawData: RawDataItem[],
    colorMapping: { [key: string]: { chartColor: string; tableColor: string } },
): DataItem[] => {
    if (!(rawData && Array.isArray(rawData) && rawData.length > 0)) return [];
    let result: DataItem[] = [];
    rawData.forEach((item) => {
        const { data, name } = item;
        const parentIndex = result.length;
        data.forEach((dataItem, index) => {
            const { kpis, metric_name, showTopBorder } = dataItem;
            index === 0
                ? result.push({
                      label: metric_name,
                      ...kpis,
                      isExpanded: false,
                      bgColor: colorMapping[name].tableColor,
                      textColor: colorConstants.blackAlt1,
                      showTopBorder,
                      showBreakevenMetrics: dataItem.has_breakeven_metrics,
                  })
                : result.push({
                      label: metric_name,
                      ...kpis,
                      parentIndex,
                      bgColor: colorMapping[name].tableColor,
                      textColor: colorConstants.greyAlt18,
                      showTopBorder,
                      showBreakevenMetrics: dataItem.has_breakeven_metrics,
                  });
        });
    });
    return result;
};
